import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit2, Trash2, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { productsService } from '@/services/products.service';
import { presentationsService, type PresentationOption } from '@/services/presentations.service';
import type { Product, ProductPresentation, ProductPresentationFormData, PresentationType, PresentationPriceHistory } from '../types/product.types';
import { PresentationPriceHistoryModal } from './PresentationPriceHistoryModal';

interface ProductPresentationsModalProps {
  isOpen: boolean;
  product: Product | null;
  onClose: () => void;
  onSave: (productId: string, presentations: ProductPresentation[]) => void;
  onPresentationsChange?: (productId: string, presentations: ProductPresentation[]) => void;
}

export const ProductPresentationsModal: React.FC<ProductPresentationsModalProps> = ({
  isOpen,
  product,
  onClose,
  onSave,
  onPresentationsChange
}) => {
  const [presentations, setPresentations] = useState<ProductPresentation[]>([]);
  const [editingPresentation, setEditingPresentation] = useState<ProductPresentation | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<ProductPresentationFormData>({
    name: 'unidad',
    isActive: true
  });
  const [priceHistoryModal, setPriceHistoryModal] = useState<{
    isOpen: boolean;
    presentation: ProductPresentation | null;
  }>({ isOpen: false, presentation: null });
  const [loading, setLoading] = useState(false);
  const [availablePresentations, setAvailablePresentations] = useState<PresentationOption[]>([]);
  const [loadingPresentations, setLoadingPresentations] = useState(false);

  // Cargar presentaciones disponibles desde la API
  useEffect(() => {
    const loadPresentations = async () => {
      if (!isOpen) return;

      setLoadingPresentations(true);
      try {
        const presentations = await presentationsService.fetchPresentations();
        setAvailablePresentations(presentations);
      } catch (error) {
        console.error('Error loading presentations:', error);
        // Fallback a opciones por defecto si falla la API
        setAvailablePresentations([
          { id: 1, name: 'Unidad', value: 'unidad', label: 'Unidad', isActive: true },
          { id: 2, name: 'Clásico', value: 'clasico', label: 'Clásico', isActive: true },
          { id: 3, name: 'Económico', value: 'economico', label: 'Económico', isActive: true },
          { id: 4, name: 'Extra', value: 'extra', label: 'Extra', isActive: true },
          { id: 5, name: '12ONZ', value: '12onz', label: '12ONZ', isActive: true },
          { id: 6, name: '16ONZ', value: '16onz', label: '16ONZ', isActive: true },
          { id: 7, name: '21ONZ', value: '21onz', label: '21ONZ', isActive: true }
        ]);
      } finally {
        setLoadingPresentations(false);
      }
    };

    loadPresentations();
  }, [isOpen]);

  useEffect(() => {
    if (product && product.presentations) {
      setPresentations([...product.presentations]);
    } else if (product) {
      // Crear presentación por defecto basada en el producto
      const defaultPresentation: ProductPresentation = {
        id: `pres-${Date.now()}`,
        name: product.presentation || 'Estándar',
        description: `Presentación estándar de ${product.name}`,
        price: product.price || 0,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setPresentations([defaultPresentation]);
    }
  }, [product]);

  const resetForm = () => {
    setFormData({
      name: 'unidad',
      isActive: true
    });
    setEditingPresentation(null);
    setShowForm(false);
  };

  const handleAddPresentation = () => {
    setEditingPresentation(null);
    setFormData({
      name: 'unidad',
      isActive: true
    });
    setShowForm(true);
  };

  const handleEditPresentation = (presentation: ProductPresentation) => {
    setEditingPresentation(presentation);

    // Encontrar el valor correspondiente al nombre de presentación
    const presentationValue = availablePresentations.find(option => option.label === presentation.name)?.value || 'unidad';

    setFormData({
      name: presentationValue,
      isActive: presentation.isActive
    });
    setShowForm(true);
  };

  const handleDeletePresentation = async (presentationId: string) => {
    if (presentations.length <= 1) {
      alert('No se puede eliminar la única presentación. El producto debe tener al menos una presentación.');
      return;
    }

    if (!product) return;

    if (!window.confirm('¿Estás seguro de que deseas eliminar esta presentación?')) {
      return;
    }

    setLoading(true);
    try {
      await productsService.deletePresentation(product.id, presentationId);

      const updatedPresentations = presentations.filter(p => p.id !== presentationId);
      setPresentations(updatedPresentations);

      // Notificar al componente padre de los cambios
      notifyPresentationsChange(updatedPresentations);
    } catch (error) {
      console.error('Error al eliminar presentación:', error);
      alert('Error al eliminar la presentación. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleManagePriceHistory = (presentation: ProductPresentation) => {
    setPriceHistoryModal({ isOpen: true, presentation });
  };

  const handleSavePrice = async (presentation: ProductPresentation, newPrice: number) => {
    // Update the presentation in the local state
    const updatedPresentations = presentations.map(p => 
      p.id === presentation.id 
        ? { ...p, price: newPrice, updatedAt: new Date().toISOString() }
        : p
    );
    setPresentations(updatedPresentations);
    
    // Notificar al componente padre de los cambios
    notifyPresentationsChange(updatedPresentations);
  };


  const handleSubmitPresentation = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !product) {
      return;
    }

    const presentationLabel = availablePresentations.find(option => option.value === formData.name)?.label || formData.name;

    // Verificar duplicados (excepto si estamos editando)
    const existingPresentation = presentations.find(p => 
      p.name === presentationLabel && (!editingPresentation || p.id !== editingPresentation.id)
    );
    
    if (existingPresentation) {
      alert('Ya existe una presentación de este tipo. Selecciona otro tipo.');
      return;
    }

    setLoading(true);
    try {
      if (editingPresentation) {
        // Editar presentación existente
        const updatedPresentation = {
          ...editingPresentation,
          name: presentationLabel,
          isActive: formData.isActive
        };

        const updated = await productsService.updatePresentation(product.id, editingPresentation.id, updatedPresentation);
        const updatedPresentations = presentations.map(p => 
          p.id === editingPresentation.id ? { ...updated.data, price: p.price } : p
        );
        setPresentations(updatedPresentations);
        
        // Notificar al componente padre de los cambios
        notifyPresentationsChange(updatedPresentations);
      } else {
        // Crear nueva presentación y guardarla inmediatamente en BD
        const selectedPresentation = availablePresentations.find(option => option.value === formData.name);
        const presentationData = {
          name: presentationLabel,
          description: `Presentación ${presentationLabel} de ${product.name}`,
          price: 0,
          isActive: formData.isActive,
          presentationId: selectedPresentation?.id || null
        };

        const newPresentation = await productsService.createPresentation(product.id, presentationData);
        const updatedPresentations = [...presentations, newPresentation.data];
        setPresentations(updatedPresentations);
        
        // Notificar al componente padre de los cambios
        notifyPresentationsChange(updatedPresentations);
      }

      setFormData({
        name: 'unidad',
        isActive: true
      });
      setEditingPresentation(null);
      setShowForm(false);
    } catch (error) {
      console.error('Error al guardar presentación:', error);
      alert('Error al guardar la presentación. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // handleSave function removed since changes are saved automatically
  
  // Función auxiliar para notificar cambios al componente padre
  const notifyPresentationsChange = (updatedPresentations: ProductPresentation[]) => {
    if (product) {
      // Usar onPresentationsChange si está disponible (solo actualiza estado local)
      // Si no, usar onSave (pero esto causará re-guardado en BD)
      if (onPresentationsChange) {
        onPresentationsChange(product.id, updatedPresentations);
      } else {
        onSave(product.id, updatedPresentations);
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-orange-600" />
            Presentaciones de: {product.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Lista de presentaciones */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Presentaciones actuales</h3>
              <Button
                onClick={handleAddPresentation}
                className="flex items-center gap-2"
                disabled={showForm}
              >
                <Plus className="w-4 h-4" />
                Agregar Presentación
              </Button>
            </div>

            <div className="space-y-3">
              {presentations.map((presentation) => (
                <div
                  key={presentation.id}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-medium">{presentation.name}</span>
                      <Badge variant={presentation.isActive ? "default" : "secondary"}>
                        {presentation.isActive ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </div>
                    <p className="text-lg font-semibold text-green-600">
                      {formatCurrency(presentation.price)}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleManagePriceHistory(presentation)}
                      disabled={showForm}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <DollarSign className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeletePresentation(presentation.id)}
                      disabled={showForm || presentations.length <= 1}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Formulario de presentación */}
          {showForm && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-medium mb-4">
                {editingPresentation ? 'Editar Presentación' : 'Nueva Presentación'}
              </h3>
              
              <form onSubmit={handleSubmitPresentation} className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Tipo de Presentación *</Label>
                    <Select value={formData.name} onValueChange={(value) => setFormData({ ...formData, name: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar presentación" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60 overflow-y-auto">
                        {loadingPresentations ? (
                          <SelectItem value="loading" disabled>
                            Cargando presentaciones...
                          </SelectItem>
                        ) : (
                          availablePresentations.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <div className="text-xs text-gray-500 mt-1">
                      El precio se configurará posteriormente en el modal de gestión de precios
                    </div>
                  </div>
                </div>



                <div className="flex items-center space-x-2">
                  <Switch
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked: boolean) => setFormData({ ...formData, isActive: checked })}
                  />
                  <Label htmlFor="isActive">Presentación activa</Label>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={!formData.name.trim() || loading}
                  >
                    {loading ? 'Guardando...' : (editingPresentation ? 'Actualizar' : 'Agregar')}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Botones de acción del modal */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cerrar
            </Button>
          </div>
        </div>

        {/* Price History Modal */}
        <PresentationPriceHistoryModal
          isOpen={priceHistoryModal.isOpen}
          presentation={priceHistoryModal.presentation}
          productName={product?.name || ''}
          onClose={() => setPriceHistoryModal({ isOpen: false, presentation: null })}
          onSavePrice={handleSavePrice}
        />
      </DialogContent>
    </Dialog>
  );
};